import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Snackbar,
  Alert,
  Stack,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useTokenMap from '../hooks/useTokenMap';
import { useBookingPeriod } from '../hooks/useBookingPeriod';
import { useGames } from '../hooks/useGames';
import FCFSBookingView from '../components/FCFSBookingView';
import LotteryBookingView from '../components/LotteryBookingView';

interface ReservationProps {
  isAdmin?: boolean;
}

const Reservation: React.FC<ReservationProps> = ({ isAdmin = false }) => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // Use custom hooks
  const { tokenMap, nextTokenExpiration, saveToken, removeToken, removeNextTokenExpiration } = useTokenMap();
  const { bookingPeriods, selectedBookingPeriod, setSelectedBookingPeriod } = useBookingPeriod();
  const { games, gamesById, loading: loadingGames } = useGames(selectedBookingPeriod);

  const handleAuthAction = async () => {
    if (user) {
      // Kirjaudu ulos
      await signOut();
      setSnackbarMessage('Kirjauduttu ulos');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } else {
      // Siirry admin-kirjautumissivulle
      navigate('/admin-login');
    }
  };

  useEffect(() => {
    if (error) {
      setSnackbarMessage(error);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setError('');
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      setSnackbarMessage(success);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setSuccess('');
    }
  }, [success]);

  const handleCloseSnackbar = (event: Event | React.SyntheticEvent<any, Event>, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleSuccess = (message: string) => {
    setSuccess(message);
  };

  const handleError = (message: string) => {
    setError(message);
  };

  const handlePreviousPeriod = () => {
    if (!selectedBookingPeriod) return;
    const currentIndex = bookingPeriods.findIndex((p) => p.id === selectedBookingPeriod.id);
    if (currentIndex < bookingPeriods.length - 1) {
      setSelectedBookingPeriod(bookingPeriods[currentIndex + 1]);
    }
  };

  const handleNextPeriod = () => {
    if (!selectedBookingPeriod) return;
    const currentIndex = bookingPeriods.findIndex((p) => p.id === selectedBookingPeriod.id);
    if (currentIndex > 0) {
      setSelectedBookingPeriod(bookingPeriods[currentIndex - 1]);
    }
  };

  if (bookingPeriods.length === 0 || !selectedBookingPeriod) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  // Detect booking period mode
  const mode = selectedBookingPeriod.mode?.toUpperCase() || 'FCFS';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8, position: 'relative', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        {/* Admin link - shown at top when logged in */}
        {user && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/admin')}
              sx={{ 
                textTransform: 'none',
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Hallinnointiin
            </Button>
          </Box>
        )}

        {/* Booking Period Navigation */}
        <Paper
          elevation={3}
          sx={{
            padding: 2,
            bgcolor: 'background.paper',
            color: 'text.primary',
            width: '100%',
            maxWidth: 'md',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Button
              onClick={handlePreviousPeriod}
              disabled={
                bookingPeriods.findIndex((p) => p.id === selectedBookingPeriod.id) ===
                bookingPeriods.length - 1
              }
              variant="outlined"
              sx={{ minWidth: 'auto', px: 1 }}
            >
              <ArrowBackIcon />
            </Button>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h6" sx={{ color: 'primary.main', textTransform: 'uppercase' }}>
                {selectedBookingPeriod.name}
              </Typography>
            </Box>
            <Button
              onClick={handleNextPeriod}
              disabled={bookingPeriods.findIndex((p) => p.id === selectedBookingPeriod.id) === 0}
              variant="outlined"
              sx={{ minWidth: 'auto', px: 1 }}
            >
              <ArrowForwardIcon />
            </Button>
          </Stack>
        </Paper>

        {/* Mode-specific view */}
        {mode === 'FCFS' ? (
          <FCFSBookingView
            selectedBookingPeriod={selectedBookingPeriod}
            games={games}
            gamesById={gamesById}
            loadingGames={loadingGames}
            tokenMap={tokenMap}
            nextTokenExpiration={nextTokenExpiration}
            saveToken={saveToken}
            removeToken={removeToken}
            removeNextTokenExpiration={removeNextTokenExpiration}
            isAdmin={isAdmin}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        ) : (
          <LotteryBookingView
            selectedBookingPeriod={selectedBookingPeriod}
            games={games}
            gamesById={gamesById}
            loadingGames={loadingGames}
            tokenMap={tokenMap}
            saveToken={saveToken}
            removeToken={removeToken}
            isAdmin={isAdmin}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        )}
      </Box>

      {/* Admin Login/Logout Button - Fixed at bottom */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
        }}
      >
        <Button
          onClick={handleAuthAction}
          variant="text"
          size="small"
          sx={{
            textTransform: 'none',
            color: 'white',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.8)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {user ? 'Kirjaudu ulos' : 'Ylläpidon kirjautuminen'}
        </Button>
      </Box>

      {/* Snackbar for success/error messages */}
      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Reservation;
